import React, { useState, useRef, useEffect } from 'react';
import jsQR from 'jsqr';
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../config/authenticatedFetch';
import './QRViewer.css';

interface RegistrationData {
  id?: string;
  external_id: string;
  name: string;
  field_1: string;  // Campo genérico 1
  field_2: string;  // Campo genérico 2
  field_3: string;  // Campo genérico 3
  photo_url?: string;
}

const QRViewer: React.FC = () => {
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [scannedCode, setScannedCode] = useState<string>('');
  const [qrDetected, setQrDetected] = useState<boolean>(false);
  const [registrationData, setRegistrationData] = useState<RegistrationData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [hasValidCode, setHasValidCode] = useState<boolean>(false);
  
  const [shouldStartScanning, setShouldStartScanning] = useState<boolean>(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // useEffect para permitir scroll nesta página
  useEffect(() => {
    // Adicionar classe para permitir scroll no html e body
    document.documentElement.classList.add('allow-scroll');
    document.body.classList.add('allow-scroll');
    
    // Debug: verificar se as classes foram aplicadas
    console.log('🔄 Classes aplicadas:', {
      html: document.documentElement.classList.contains('allow-scroll'),
      body: document.body.classList.contains('allow-scroll')
    });
    
    return () => {
      // Remover classe ao sair da página
      document.documentElement.classList.remove('allow-scroll');
      document.body.classList.remove('allow-scroll');
      
      console.log('🔄 Classes removidas - scroll bloqueado novamente');
      
      // Cleanup da câmera
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
      }
    };
  }, []);

  // useEffect para inicializar câmera quando o DOM está pronto
  useEffect(() => {
    if (shouldStartScanning && videoRef.current && canvasRef.current) {
      initializeCamera();
    }
  }, [shouldStartScanning, isScanning]);

  const initializeCamera = async () => {
    try {
      console.log('🚀 Inicializando câmera...');
      
      // Verificar se getUserMedia está disponível
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Câmera não suportada neste navegador');
        setIsScanning(false);
        setShouldStartScanning(false);
        return;
      }

      console.log('📱 Solicitando acesso à câmera...');
      
      const constraints = {
        video: {
          facingMode: { ideal: 'environment' }, // Preferir câmera traseira
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('✅ Stream da câmera obtido:', stream);
      
      streamRef.current = stream;
      
      // Aguardar o video estar pronto
      const video = videoRef.current!;
      video.srcObject = stream;
      
      // Aguardar o vídeo carregar
      video.onloadedmetadata = () => {
        console.log('📹 Metadados do vídeo carregados');
        video.play().then(() => {
          console.log('▶️ Vídeo iniciado com sucesso');
          
          // Iniciar detecção de QR code com frequência otimizada (3x por segundo)
          scanIntervalRef.current = setInterval(() => {
            captureFrame();
          }, 330); // ~3 FPS para melhor performance
          
          setShouldStartScanning(false); // Reset flag
          
        }).catch((playError) => {
          console.error('❌ Erro ao reproduzir vídeo:', playError);
          setError('Erro ao iniciar visualização da câmera');
          stopScanning();
        });
      };

    } catch (err: any) {
      console.error('❌ Erro ao acessar câmera:', err);
      
      let errorMessage = 'Erro ao acessar a câmera';
      
      if (err.name === 'NotAllowedError') {
        errorMessage = 'Permissão para acessar a câmera foi negada. Permita o acesso e tente novamente.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'Nenhuma câmera foi encontrada no dispositivo.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Câmera está sendo usada por outro aplicativo.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'Câmera não atende aos requisitos especificados.';
      }
      
      setError(errorMessage);
      setIsScanning(false);
      setShouldStartScanning(false);
    }
  };

  const startScanning = async () => {
    try {
      setError('');
      setSuccess('');
      setQrDetected(false); // Reset QR detection state
      setIsScanning(true);
      console.log('🚀 Iniciando scanner...');
      
      // Aguardar um breve momento para o DOM se atualizar
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (!videoRef.current || !canvasRef.current) {
        console.error('❌ Referências de video ou canvas não encontradas após renderização');
        setError('Erro interno: elementos não encontrados');
        setIsScanning(false);
        return;
      }

      // Verificar se getUserMedia está disponível
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Câmera não suportada neste navegador');
        setIsScanning(false);
        return;
      }

      console.log('📱 Solicitando acesso à câmera...');
      
      const constraints = {
        video: {
          facingMode: { ideal: 'environment' }, // Preferir câmera traseira
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('✅ Stream da câmera obtido:', stream);
      
      streamRef.current = stream;
      
      // Aguardar o video estar pronto
      const video = videoRef.current;
      video.srcObject = stream;
      
      // Aguardar o vídeo carregar
      video.onloadedmetadata = () => {
        console.log('📹 Metadados do vídeo carregados');
        video.play().then(() => {
          console.log('▶️ Vídeo iniciado com sucesso');
          
          // Iniciar detecção de QR code com frequência otimizada (3x por segundo)
          scanIntervalRef.current = setInterval(() => {
            captureFrame();
          }, 330); // ~3 FPS para melhor performance
          
        }).catch((playError) => {
          console.error('❌ Erro ao reproduzir vídeo:', playError);
          setError('Erro ao iniciar visualização da câmera');
          stopScanning();
        });
      };

    } catch (err: any) {
      console.error('❌ Erro ao acessar câmera:', err);
      
      let errorMessage = 'Erro ao acessar a câmera';
      
      if (err.name === 'NotAllowedError') {
        errorMessage = 'Permissão para acessar a câmera foi negada. Permita o acesso e tente novamente.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'Nenhuma câmera foi encontrada no dispositivo.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Câmera está sendo usada por outro aplicativo.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'Câmera não atende aos requisitos especificados.';
      }
      
      setError(errorMessage);
      setIsScanning(false);
    }
  };

  const captureFrame = () => {
    if (!videoRef.current || !canvasRef.current) {
      console.warn('⚠️ Video ou canvas não disponível para captura');
      return;
    }
    
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext('2d');
    
    if (!context) {
      console.warn('⚠️ Contexto 2D do canvas não disponível');
      return;
    }
    
    // Verificar se o vídeo tem dimensões válidas
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      console.warn('⚠️ Vídeo ainda não tem dimensões válidas');
      return;
    }
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Obter dados da imagem para processamento de QR code
    try {
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      
      // Tentar detectar QR code usando jsQR
      const qrCode = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: "dontInvert",
      });
      
      if (qrCode) {
        console.log('🎯 QR Code detectado:', qrCode.data);
        
        // Indicar que um QR foi detectado
        setQrDetected(true);
        
        // Parar o scanning quando detectar um QR code
        stopScanning();
        
        // Processar o QR code detectado
        handleQRCodeDetected(qrCode.data);
        return; // Sair da função após detectar
      }
      
      // Log periódico para debug (a cada 20 tentativas - ~20 segundos)
      if (Math.random() < 0.05) {
        console.log(`📸 Frame processado: ${imageData.width}x${imageData.height}, nenhum QR code encontrado`);
      }
      
    } catch (error) {
      console.warn('⚠️ Erro ao processar frame para QR code:', error);
    }
  };

  const stopScanning = () => {
    console.log('⏹️ Parando scanner...');
    
    if (streamRef.current) {
      console.log('📷 Parando tracks da câmera');
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log(`🔒 Track ${track.kind} parado`);
      });
      streamRef.current = null;
    }
    
    if (scanIntervalRef.current) {
      console.log('⏰ Limpando interval de scan');
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    
    if (videoRef.current) {
      console.log('📺 Limpando srcObject do vídeo');
      videoRef.current.srcObject = null;
    }
    
    setIsScanning(false);
    console.log('✅ Scanner parado com sucesso');
  };

  const handleManualCodeInput = (code: string) => {
    if (code.trim()) {
      setScannedCode(code.trim());
      handleQRCodeDetected(code.trim());
    }
  };

  const testCameraPermissions = async () => {
    try {
      console.log('🔍 Testando permissões da câmera...');
      
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('API de mídia não suportada neste navegador');
        return;
      }

      // Listar dispositivos disponíveis
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      
      console.log('📱 Dispositivos de vídeo encontrados:', videoDevices.length);
      videoDevices.forEach((device, index) => {
        console.log(`📹 Dispositivo ${index + 1}: ${device.label || 'Câmera ' + (index + 1)}`);
      });

      if (videoDevices.length === 0) {
        setError('Nenhuma câmera encontrada no dispositivo');
        return;
      }

      // Testar acesso básico
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      console.log('✅ Permissão concedida, câmera acessível');
      
      // Parar imediatamente o teste
      stream.getTracks().forEach(track => track.stop());
      
      setSuccess('✅ Câmera testada com sucesso! Você pode usar o scanner.');
      
    } catch (err: any) {
      console.error('❌ Erro no teste da câmera:', err);
      let errorMessage = 'Erro ao testar câmera: ';
      
      if (err.name === 'NotAllowedError') {
        errorMessage += 'Permissão negada. Permita o acesso à câmera nas configurações do navegador.';
      } else if (err.name === 'NotFoundError') {
        errorMessage += 'Nenhuma câmera encontrada.';
      } else {
        errorMessage += err.message;
      }
      
      setError(errorMessage);
    }
  };

  const handleQRCodeDetected = async (code: string) => {
    setLoading(true);
    setError('');
    setSuccess('');
    setScannedCode(code); // Salvar o código escaneado

    try {
      console.log('🔍 QR Code processando:', code);
      
      // Tentar extrair ID do participante de diferentes formatos de QR code
      let participantId = code;
      
      // Se for JSON, tentar extrair o ID
      try {
        const jsonData = JSON.parse(code);
        if (jsonData.idParticipante) {
          participantId = jsonData.idParticipante;
        } else if (jsonData.id) {
          participantId = jsonData.id;
        } else if (jsonData.external_id) {
          participantId = jsonData.external_id;
        }
        console.log('📋 ID extraído do JSON:', participantId);
      } catch (e) {
        // Não é JSON, usar como string simples
        console.log('📝 Usando código como string simples:', participantId);
      }

      console.log('🔎 Buscando registro para ID:', participantId);
      const response = await authenticatedFetch(`${API_ENDPOINTS.API_GETBYID}?idParticipante=${participantId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();
      console.log('📊 Resposta da API:', data);

      if (data.success && data.data) {
        // Registro encontrado
        setRegistrationData(data.data);
        setSuccess('✅ Registro encontrado! Dados carregados para visualização.');
        setHasValidCode(true); // Marca que um código válido foi processado
      } else {
        // Registro não encontrado
        setRegistrationData(null);
        setError('🔍 Nenhum registro encontrado para este código.');
        setHasValidCode(true); // Mesmo assim marca como código válido (foi processado)
      }
    } catch (err) {
      console.error('❌ Erro ao buscar registro:', err);
      setError('Erro ao buscar registro no servidor. Verifique a conexão.');
    } finally {
      setLoading(false);
    }
  };

  const clearData = () => {
    setScannedCode('');
    setRegistrationData(null);
    setError('');
    setSuccess('');
    setHasValidCode(false); // Reset da validação do código
  };

  return (
    <div className="qr-viewer-container">
      <div className="qr-viewer-header">
        <h1>👀 Visualizador QR</h1>
        <p>Escaneie um QR code para visualizar informações do participante</p>
      </div>

      {/* Scanner Section */}
      <div className="scanner-section">
        <div className="scanner-controls">
          <div className="button-group">
            <button
              onClick={testCameraPermissions}
              className="btn btn-outline"
              disabled={loading}
            >
              🔧 Testar Câmera
            </button>
            
            {!isScanning ? (
              <button 
                onClick={startScanning}
                className="btn btn-primary"
                disabled={loading}
              >
                📷 Iniciar Scanner
              </button>
            ) : (
              <button 
                onClick={stopScanning}
                className="btn btn-secondary"
              >
                ⏹️ Parar Scanner
              </button>
            )}
          </div>
        </div>

        {/* Input manual para teste */}
        <div className="manual-input-section">
          <h3>Ou digite o código manualmente:</h3>
          <div className="manual-input-group">
            <input
              type="text"
              placeholder="Digite o código (ex: USER_002)"
              className="form-control"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleManualCodeInput((e.target as HTMLInputElement).value);
                  (e.target as HTMLInputElement).value = '';
                }
              }}
            />
            <button
              onClick={() => {
                const input = document.querySelector('.manual-input-group input') as HTMLInputElement;
                if (input) {
                  handleManualCodeInput(input.value);
                  input.value = '';
                }
              }}
              className="btn btn-primary"
              disabled={loading}
            >
              🔍 Buscar
            </button>
          </div>
        </div>

        {isScanning && (
          <div className="scanner-container">
            <video 
              ref={videoRef}
              className="scanner-video"
              autoPlay
              playsInline
              muted
            />
            <canvas 
              ref={canvasRef}
              style={{ display: 'none' }}
            />
            <div className="scanner-overlay">
              <div className={`scanner-frame ${qrDetected ? 'detected' : ''}`}></div>
              <p>{qrDetected ? '✅ QR Code Detectado!' : 'Posicione o QR code dentro do quadro'}</p>
            </div>
          </div>
        )}

        {scannedCode && (
          <div className="scanned-result">
            <strong>Código detectado:</strong> {scannedCode}
          </div>
        )}
      </div>

      {/* Messages */}
      {loading && <div className="message loading">⏳ Processando...</div>}
      {error && <div className="message error">❌ {error}</div>}
      {success && <div className="message success">✅ {success}</div>}
      
      {/* Warning message when no valid code */}
      {!hasValidCode && !loading && (
        <div className="message warning">
          ⚠️ É necessário escanear um QR code ou inserir um código manualmente para visualizar as informações.
        </div>
      )}

      {/* Data Display Section */}
      {registrationData && hasValidCode && (
        <div className="data-section">
          <div className="data-header">
            <h2>📋 Informações do Participante</h2>
            <div className="record-info">
              <small>ID: {registrationData.id}</small>
            </div>
          </div>

          <div className="participant-info">
            <div className="info-row">
              <div className="info-group">
                <label>ID Participante:</label>
                <div className="info-value">{registrationData.external_id}</div>
              </div>
              <div className="info-group">
                <label>Nome:</label>
                <div className="info-value">{registrationData.name || 'Não informado'}</div>
              </div>
            </div>

            <div className="info-row">
              <div className="info-group">
                <label>Campo 2:</label>
                <div className="info-value">{registrationData.field_2 || 'Não informado'}</div>
              </div>
            </div>

            <div className="info-row">
              <div className="info-group">
                <label>Campo 1:</label>
                <div className="info-value phrase">
                  {registrationData.field_1 || 'Não informado'}
                </div>
              </div>
              <div className="info-group">
                <label>Campo 3:</label>
                <div className="info-value phrase">
                  {registrationData.field_3 || 'Não informado'}
                </div>
              </div>
            </div>

            {registrationData.photo_url && (
              <div className="info-group photo-section">
                <label>Foto:</label>
                <div className="photo-display">
                  <img 
                    src={registrationData.photo_url} 
                    alt="Foto do participante" 
                    className="participant-photo"
                  />
                </div>
              </div>
            )}

            <div className="actions">
              <button
                onClick={clearData}
                className="btn btn-outline"
              >
                🗑️ Limpar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRViewer;
